import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, Calculator, Package, PenTool } from "lucide-react";
import { Route as RouteIcon } from "lucide-react";
import Hero from "@/components/Hero";
import AtelierMarquee from "@/components/AtelierMarquee";
import FaqContactSection from "@/components/FaqContactSection";
import Reveal from "@/components/Reveal";

const SHORTCUTS = [
  {
    to: "/services",
    icon: PenTool,
    title: "Services",
    desc: "Modélisation 3D sur mesure, réparation de pièces cassées et impression 3D à la demande.",
  },
  {
    to: "/realisations",
    icon: Package,
    title: "Exemples de réalisations",
    desc: "Organizer de tiroir, rangements de cuisine, aménagements camping-car et plus.",
  },
  {
    to: "/process",
    icon: RouteIcon,
    title: "Notre process",
    desc: "Devis sur mesure, validation par PDF, paiement puis livraison de vos fichiers.",
  },
  {
    to: "/devis",
    icon: Calculator,
    title: "Demander un devis",
    desc: "Décrivez votre projet en 2 minutes — réponse sous 48 h ouvrées.",
  },
];

export default function HomePage() {
  const { hash } = useLocation();

  useEffect(() => {
    document.title = "TroiDéo — Impression & Modélisation 3D sur demande";
  }, []);

  useEffect(() => {
    if (!hash) return;
    const t = setTimeout(() => {
      const el = document.querySelector(hash);
      if (!el) return;
      if (window.__lenis) window.__lenis.scrollTo(el, { offset: -72 });
      else el.scrollIntoView({ behavior: "smooth" });
    }, 350);
    return () => clearTimeout(t);
  }, [hash]);

  return (
    <>
      <Hero />
      <AtelierMarquee />

      <section className="py-20 lg:py-24" data-testid="home-shortcuts">
        <div className="mx-auto max-w-7xl px-6 lg:px-12">
          <Reveal>
            <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-accent">Explorer l'atelier</span>
            <h2 className="mt-3 font-display text-2xl font-extrabold tracking-tight text-ink sm:text-3xl">
              Impression & modélisation 3D, de A à Z.
            </h2>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {SHORTCUTS.map(({ to, icon: Icon, title, desc }, i) => (
              <Reveal key={to} delay={i * 0.06} className="h-full">
                <Link
                  to={to}
                  data-testid={`home-shortcut-${to.slice(1) || "home"}`}
                  className="group flex h-full flex-col rounded-[20px] border border-black/10 bg-white p-6 transition-all duration-200 hover:-translate-y-1 hover:border-ink hover:shadow-[0_16px_40px_-24px_rgba(0,0,0,0.3)]"
                >
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-orange-50">
                    <Icon className="h-5 w-5 text-accent" />
                  </span>
                  <span className="mt-4 font-display text-base font-bold tracking-tight text-ink">{title}</span>
                  <span className="mt-1.5 flex-1 text-sm leading-relaxed text-neutral-600">{desc}</span>
                  <span className="mt-4 inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-accent">
                    Voir
                    <ArrowRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-1" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <FaqContactSection />
    </>
  );
}
